<template>
  <div class="py-4 container-fluid">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header pb-0">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
              <h6 class="mb-0"><i class="fas fa-hammer me-1 text-success"></i> Producción — Pedidos en proceso</h6>
              <div class="d-flex gap-2">
                <button
                  type="button"
                  class="btn btn-sm"
                  style="background-color: #6f7a93; border-color: #6f7a93; color: #fff;"
                  :disabled="downloadingPending"
                  @click="downloadPendingItems"
                >
                  <i class="fas" :class="downloadingPending ? 'fa-spinner fa-spin' : 'fa-file-pdf'"></i>
                  PDF prod.
                </button>
              </div>
            </div>
            <p class="text-xs text-secondary mb-3">
              Pedidos de las ventas (remisiones) con productos en estado de fabricación «En proceso».
            </p>
          </div>

          <div class="card-body px-0 pt-0 pb-2">
            <div v-if="loading" class="text-center py-5">
              <i class="fas fa-spinner fa-spin fa-2x text-success"></i>
              <p class="mt-2 text-sm text-secondary">Cargando pedidos...</p>
            </div>

            <div v-else class="table-responsive p-0">
              <table class="table align-items-center mb-0">
                <thead>
                  <tr>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7" style="width: 40px">
                      <input
                        type="checkbox"
                        style="width: 16px; height: 16px; cursor: pointer; accent-color: #2dce89;"
                        :checked="allSelected"
                        title="Seleccionar todos los pedidos de la página"
                        @change="toggleSelectAll"
                      />
                    </th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Pedido</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Cliente</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Fecha</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Estado</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">En proceso</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Tareas</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Total a pagar</th>
                    <th class="text-center text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Opciones</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="pedido in data" :key="pedido.id">
                    <td class="text-center align-middle">
                      <input
                        type="checkbox"
                        style="width: 16px; height: 16px; cursor: pointer; accent-color: #2dce89;"
                        :value="pedido.id"
                        v-model="selectedIds"
                      />
                    </td>
                    <td class="align-middle ps-2">
                      <span class="text-sm font-weight-bold">#{{ pedido.id }}</span>
                    </td>
                    <td class="align-middle">
                      <div class="d-flex px-2 py-1 d-flex flex-column justify-content-center">
                        <h6 class="mb-0 text-sm">{{ pedido.name || 'Cliente' }}</h6>
                        <p v-if="pedido.phone" class="text-xs text-secondary mb-0">{{ pedido.phone }}</p>
                      </div>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs text-secondary">{{ formatDate(pedido.created_at) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <span :class="['badge badge-sm', preparadoBadgeClass(pedido.preparado)]">
                        {{ preparadoLabel(pedido.preparado) }}
                      </span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="badge badge-sm bg-warning text-dark">{{ pedido.details.length }} prod.</span>
                      <span class="badge badge-sm bg-secondary ms-1">{{ pedidoPairs(pedido) }} pares</span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs">
                        {{ completedTasks(pedido) }}/{{ totalTasks(pedido) }}
                        <i v-if="totalTasks(pedido) > 0 && completedTasks(pedido) === totalTasks(pedido)" class="fas fa-check-circle text-success ms-1" title="Tareas completadas"></i>
                      </span>
                    </td>
                    <td class="text-center align-middle">
                      <span class="text-xs font-weight-bold">{{ money(pedidoTotal(pedido)) }}</span>
                    </td>
                    <td class="text-center align-middle">
                      <router-link
                        :to="{ name: 'Production Order', query: { id: pedido.id } }"
                        class="btn btn-outline-success btn-sm mb-0"
                        title="Ver producción del pedido"
                      >
                        <i class="fas fa-eye me-1"></i> Ver pedido
                      </router-link>
                    </td>
                  </tr>

                  <tr v-if="data.length === 0">
                    <td colspan="9" class="text-center text-xxs">No hay pedidos con productos en proceso</td>
                  </tr>
                </tbody>
              </table>

              <!-- Paginación -->
              <div class="pagination p-3">
                <a
                  v-for="link in links"
                  :key="link.label"
                  class="page-item cursor-pointer"
                  @click="fetchData(link.url)"
                  :disabled="!link.url || link.active"
                  :class="{ active: link.label === currentPage.toString() }"
                >
                  {{ link.label }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import remissionsService from '@/services/remissions.service';
import remissionsDetailsService from '@/services/remissionsDetails.service';

export default {
  name: 'ProductionInProgress',
  data() {
    return {
      data: [],
      links: [],
      currentPage: 1,
      lastPage: 1,
      page: 1,
      loading: true,
      selectedIds: [],
      downloadingPending: false,
    };
  },
  computed: {
    allSelected() {
      return this.data.length > 0 && this.data.every((d) => this.selectedIds.includes(d.id));
    },
  },
  methods: {
    async fetchData(pageUrl = null) {
      this.loading = true;
      if (pageUrl) {
        const parsed = parseInt(new window.URL(pageUrl).searchParams.get('page'));
        this.page = Number.isNaN(parsed) ? 1 : parsed;
      }

      try {
        const res = await remissionsDetailsService.getInProgress(this.page);
        this.data = res.data.data || [];
        this.links = res.data.links || [];
        this.currentPage = res.data.current_page || 1;
        this.lastPage = res.data.last_page || 1;
        this.selectedIds = [];
      } catch (error) {
        console.error('Error al cargar pedidos en proceso:', error);
        await Swal.fire('Error', 'No se pudo cargar la producción en proceso', 'error');
      } finally {
        this.loading = false;
      }
    },
    toggleSelectAll(event) {
      if (event.target.checked) {
        this.selectedIds = this.data.map((d) => d.id);
      } else {
        this.selectedIds = [];
      }
    },
    formatDate(value) {
      if (!value) return '—';
      return new Date(value).toLocaleDateString('es-ES');
    },
    preparadoLabel(preparado) {
      switch (parseInt(preparado)) {
        case 1: return 'Preparado';
        case 2: return 'Terminado';
        case 3: return 'En proceso';
        case 4: return 'Fabricado';
        case 5: return 'Despachado';
        default: return 'Pendiente';
      }
    },
    preparadoBadgeClass(preparado) {
      switch (parseInt(preparado)) {
        case 1: return 'bg-info text-dark';
        case 2: return 'bg-success';
        case 3: return 'bg-warning text-dark';
        case 4: return 'bg-primary';
        case 5: return 'bg-secondary';
        default: return 'bg-secondary';
      }
    },
    pedidoPairs(pedido) {
      return (pedido.details || []).reduce((sum, d) => sum + (parseInt(d.quantity) || 0), 0);
    },
    totalTasks(pedido) {
      return (pedido.details || []).reduce((sum, d) => sum + ((d.assignments || []).length), 0);
    },
    completedTasks(pedido) {
      return (pedido.details || []).reduce(
        (sum, d) => sum + (d.assignments || []).filter((a) => a.status === 'completed').length,
        0
      );
    },
    pedidoTotal(pedido) {
      return (pedido.details || []).reduce(
        (sum, d) => sum + (d.assignments || []).reduce((s, a) => s + (parseFloat(a.total) || 0), 0),
        0
      );
    },
    money(value) {
      return (value || 0).toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    },
    async downloadPendingItems() {
      if (this.selectedIds.length === 0) {
        await Swal.fire('Atención', 'Seleccione al menos un pedido', 'warning');
        return;
      }
      this.downloadingPending = true;
      try {
        const response = await remissionsService.getPendingItems(this.selectedIds);
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `pendientes_creacion_${new Date().toISOString().slice(0, 10)}.pdf`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error al generar PDF de pendientes:', error);
        await Swal.fire('Error', 'No se pudo generar el PDF de pendientes', 'error');
      } finally {
        this.downloadingPending = false;
      }
    },
  },
  async mounted() {
    await this.fetchData();
  },
};
</script>

<style scoped>
.pagination {
  display: flex;
  justify-content: center;
  margin-top: 20px;
}

.page-item {
  margin: 0 5px;
  padding: 4px 10px;
  cursor: pointer;
  border-radius: 6px;
}

.page-item.active {
  font-weight: bold;
  background: #2dce89;
  color: #fff;
}

.page-item[disabled] {
  opacity: 0.5;
  pointer-events: none;
}
</style>
